import { useEffect, useMemo } from 'react';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getMyTeamReviewHref } from 'lib/supervisors';
import { PageInnerContent, PageSidebar } from 'components/page';
import { MyTeamPage } from 'components/supervisors';

import type { IconProp } from 'components/common/icons/Icon';

const MyTeamReviewPage = ({ activeLink = '', children }) => {
  const { messages, serverConfig, supervisorUnreviewedItems, user } =
    useReduxState();

  const { data, loading, updateData } = useApiData({
    url: `/admin/supervisors/${user?.id}/supervisees/summary`
  });

  const achievementsVisible = !serverConfig.hideAchievementsFromUI;
  const coursesVisible = !serverConfig.hideCoursesFromUI;
  const pathsVisible = !serverConfig.hidePathsFromUI;

  const countsSignal = useMemo<number>(() => {
    const numberOfUnreviewedItems =
      (data?.numberOfExpiringCompletionsUnreviewed || 0) +
      (data?.numberOfExpiredCompletionsUnreviewed || 0);
    return numberOfUnreviewedItems;
  }, [data]);

  useEffect(() => {
    if (loading) return;
    if (countsSignal === supervisorUnreviewedItems) return;
    updateData();
  }, [loading, supervisorUnreviewedItems]);

  const basePath = '/my-team/review';

  const sidebarLinks = [
    ...(coursesVisible || achievementsVisible
      ? [
          {
            as: `${basePath}/expiring-completions`,
            href: `${basePath}/expiring-completions`,
            icon: ['far', 'calendar-alt'] as IconProp,
            title: messages.myTeamReviewExpiring,
            countItems: data?.numberOfExpiringCompletions,
            countItemsUnread: data?.numberOfExpiringCompletionsUnreviewed
          }
        ]
      : []),
    ...(coursesVisible || achievementsVisible
      ? [
          {
            as: `${basePath}/expired-completions`,
            href: `${basePath}/expired-completions`,
            icon: ['far', 'calendar-times'] as IconProp,
            title: messages.myTeamReviewExpired,
            countItems: data?.numberOfExpiredCompletions,
            countItemsUnread: data?.numberOfExpiredCompletionsUnreviewed
          }
        ]
      : []),
    ...(coursesVisible || pathsVisible
      ? [
          {
            as: `${basePath}/upcoming-target-schedules`,
            href: `${basePath}/upcoming-target-schedules`,
            icon: ['far', 'clock'] as IconProp,
            title: messages.commonUpcomingTargetSchedules,
            countItems: data?.numberOfUpcomingDeadlines
          }
        ]
      : []),
    ...(coursesVisible || pathsVisible
      ? [
          {
            as: `${basePath}/overdue-target-schedules`,
            href: `${basePath}/overdue-target-schedules`,
            icon: ['far', 'times-circle'] as IconProp,
            title: messages.commonOverdueTargetSchedules,
            countItems: data?.numberOfOverdueDeadlines
          }
        ]
      : [])
  ];

  return (
    <MyTeamPage
      activeLink={getMyTeamReviewHref({ relative: true, serverConfig })}
      withSidebar={true}
    >
      <PageSidebar
        active={`${basePath}${activeLink}`}
        isWide={true}
        links={sidebarLinks}
      />
      <PageInnerContent>{children}</PageInnerContent>
    </MyTeamPage>
  );
};

export default MyTeamReviewPage;
